/**
 * "Recepção", the day of the party.
 *
 * Two jobs, and they are not equally urgent. One is the headcount: who
 * actually came, which the buffet and the goodie bags care about. The other is
 * knowing which children are in the room right now, which is the one that
 * matters at six in the evening when a family is leaving and nobody is certain
 * whose child is by the door.
 *
 * So the child count is the largest thing on the screen, and "ainda aqui" is
 * one tap away.
 *
 * Written for a phone held in one hand, at a door, with a queue. Every action
 * is a single tap, applies immediately, and can be undone by tapping again.
 */

import { useEffect, useMemo, useState } from 'react';
import { host, type Attendance, type ReceptionHousehold, type ReceptionPerson } from '../api.js';

type Filter = 'todos' | 'aqui' | 'faltam' | 'sairam';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'todos', label: 'Todos' },
  { key: 'aqui', label: 'Aqui agora' },
  { key: 'faltam', label: 'Não chegaram' },
  { key: 'sairam', label: 'Já saíram' },
];

export function Reception({ eventId }: { eventId: string }) {
  const [households, setHouseholds] = useState<ReceptionHousehold[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('todos');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    host
      .reception(eventId)
      .then((view) => setHouseholds(view.households))
      .catch((err: Error) => setError(err.message));
  }, [eventId]);

  const people = useMemo(
    () => (households ?? []).flatMap((household) => household.people),
    [households],
  );

  const counts = useMemo(() => {
    let childrenHere = 0;
    let adultsHere = 0;
    let left = 0;
    let missing = 0;

    for (const person of people) {
      if (person.attendance === 'here') {
        if (isChild(person)) childrenHere++;
        else adultsHere++;
      } else if (person.attendance === 'left') {
        left++;
      } else if (
        person.rsvpState === 'attending_staying' ||
        person.rsvpState === 'attending_dropoff'
      ) {
        missing++;
      }
    }

    return { childrenHere, adultsHere, left, missing };
  }, [people]);

  /** Apply on screen first: at a door, a spinner per tap is unusable. */
  function apply(updated: ReceptionPerson[]) {
    setHouseholds((current) =>
      (current ?? []).map((household) => ({
        ...household,
        people: household.people.map((person) => {
          const next = updated.find((p) => p.personId === person.personId);
          return next ? { ...person, ...next } : person;
        }),
      })),
    );
  }

  async function setOne(person: ReceptionPerson, state: Attendance) {
    const before = person;
    apply([{ ...person, attendance: state }]);
    setBusy(person.personId);
    try {
      const { person: saved } = await host.checkIn(
        eventId,
        person.householdId,
        person.personId,
        state,
      );
      apply([saved]);
    } catch {
      apply([before]);
      setError('Não deu para salvar. Tente de novo.');
    } finally {
      setBusy(null);
    }
  }

  async function setFamily(household: ReceptionHousehold, state: Attendance) {
    const before = household.people;
    apply(household.people.map((person) => ({ ...person, attendance: state })));
    setBusy(household.householdId);
    try {
      const { people: saved } = await host.checkInFamily(
        eventId,
        household.householdId,
        state,
      );
      apply(saved);
    } catch {
      apply(before);
      setError('Não deu para salvar. Tente de novo.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !households) {
    return (
      <div className="page">
        <div className="banner error">{error}</div>
      </div>
    );
  }

  if (!households) return <div className="spinner">Carregando…</div>;

  const term = search.trim().toLowerCase();
  const visible = households
    .map((household) => ({
      ...household,
      people: household.people.filter((person) => matches(person, filter)),
    }))
    .filter((household) => household.people.length > 0)
    .filter(
      (household) =>
        term === '' ||
        household.label.toLowerCase().includes(term) ||
        household.people.some((person) =>
          person.name.toLowerCase().includes(term),
        ),
    );

  return (
    <div className="page">
      <a href={`/painel/${eventId}`}>← Painel</a>
      <h1>Recepção</h1>

      {/*
        * The child count is the headline because it is the one somebody asks
        * out loud: "quantas crianças ainda estão aqui?"
        */}
      <div className="door-count">
        <div className="n">{counts.childrenHere}</div>
        <div className="label">
          criança{counts.childrenHere === 1 ? '' : 's'} aqui agora
        </div>
      </div>

      <div className="counts">
        <Small n={counts.adultsHere} label="adultos aqui" />
        <Small n={counts.missing} label="ainda não chegaram" />
        <Small n={counts.left} label="já saíram" />
        <Small n={counts.childrenHere + counts.adultsHere} label="pessoas na festa" />
      </div>

      {error && <div className="banner error">{error}</div>}

      <input
        type="search"
        placeholder="Buscar família ou pessoa"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginTop: '1rem' }}
      />

      <div className="filters">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            className={`chip ${filter === key ? 'on' : ''}`}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {visible.length === 0 && (
        <p className="muted" style={{ marginTop: '1rem' }}>
          Ninguém nesta lista.
        </p>
      )}

      {visible.map((household) => (
        <div className="card" key={household.householdId}>
          <div className="row" style={{ borderBottom: 0, paddingBottom: 0 }}>
            <div className="grow">
              <div className="label-main">{household.label}</div>
              {household.hasDietaryRestriction && (
                <div className="muted">
                  restrição alimentar
                  {household.dietaryNote ? `: ${household.dietaryNote}` : ''}
                </div>
              )}
            </div>
            <button
              disabled={busy === household.householdId}
              onClick={() =>
                setFamily(
                  household,
                  household.people.every((p) => p.attendance === 'here')
                    ? 'left'
                    : 'here',
                )
              }
            >
              {household.people.every((p) => p.attendance === 'here')
                ? 'Saiu tudo'
                : 'Chegou tudo'}
            </button>
          </div>

          {household.people.map((person) => (
            <div className="person" key={person.personId}>
              {/* Name above, buttons below: a name squeezed beside two buttons
                  on a phone is a name nobody can read. */}
              <div className="door-person">
                <div className="grow">
                  <div className="person-name">
                    {person.name}
                    {isChild(person) && (
                      <span className="person-role"> · criança</span>
                    )}
                  </div>
                  <div className="muted">{statusLine(person)}</div>
                </div>

                <div className="door-actions">
                  {person.attendance === 'here' ? (
                    <button
                      className="primary"
                      disabled={busy === person.personId}
                      onClick={() => setOne(person, 'left')}
                    >
                      Saiu
                    </button>
                  ) : (
                    <button
                      className={person.attendance === 'left' ? '' : 'primary'}
                      disabled={busy === person.personId}
                      onClick={() => setOne(person, 'here')}
                    >
                      {person.attendance === 'left' ? 'Voltou' : 'Chegou'}
                    </button>
                  )}

                  {person.attendance !== 'expected' && (
                    <button
                      className="linkish"
                      disabled={busy === person.personId}
                      onClick={() => setOne(person, 'expected')}
                    >
                      desfazer
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function matches(person: ReceptionPerson, filter: Filter): boolean {
  if (filter === 'todos') return true;
  if (filter === 'aqui') return person.attendance === 'here';
  if (filter === 'sairam') return person.attendance === 'left';
  return (
    person.attendance === 'expected' &&
    (person.rsvpState === 'attending_staying' ||
      person.rsvpState === 'attending_dropoff')
  );
}

function statusLine(person: ReceptionPerson): string {
  if (person.attendance === 'here') return `chegou ${clock(person.arrivedAt)}`;
  if (person.attendance === 'left') return `saiu ${clock(person.leftAt)}`;
  if (person.rsvpState === 'attending_staying') return 'disse que vem e fica';
  if (person.rsvpState === 'attending_dropoff') return 'disse que deixa e volta';
  if (person.rsvpState === 'not_attending') return 'disse que não vem';
  return 'não respondeu';
}

function clock(at: string | null): string {
  if (!at) return '';
  const date = new Date(at);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Same rule the server counts by: an age when we have one, the role if not. */
function isChild(person: ReceptionPerson): boolean {
  if (person.ageYears !== null) return person.ageYears < 13;
  return person.role === 'anchor_child' || person.role === 'sibling';
}

function Small({ n, label }: { n: number; label: string }) {
  return (
    <div className="count">
      <div className="n">{n}</div>
      <div className="label">{label}</div>
    </div>
  );
}
